import { Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { ENTITLEMENTS } from '../../application-core/constants';
import ActionText from '../../core/ActionText';
import Information from '../../core/Information';
import { UserConsumer } from '../../core/providers/UserProvider';
import { globalStyles } from '../../core/style/gstheme';
import { STATUS_PENDING, STATUS_REJECTED } from '../../core/utils/constants';
import withGS from '../../core/withGShoc';
import GS from '../../grafsync/grafsync';
import AddSuggestionDialog from './AddSuggestionDialog';
import SuggestionsViewer from './SuggestionsViewer';

const styles = Object.assign({
    existingSuggestions: {
        textAlign: 'right',
        marginTop: -55,
        marginBottom: 12
    },
    noSuggestions: {
        textAlign: 'center'
    }
}, globalStyles);

class SuggestionsManager extends React.Component {

    // By adding status on insert we avoid potential OT conflicts
    // Essentially status is functional - different users may reject/accept at the same time
    addSuggestion = user => async (suggestionType, lang, text, suggestionTargetConcept, suggestionParentConcept, suggestedLabel, assignee, notify) => {
        let suggestionInsertIRI = null;
        let suggestionLabelInsertIRI = null;
        if (suggestionType === "NewConceptSuggestion") {
            // We compute IRIs. There is no guarantee these will be unique at time of acceptance
            // But we compute now based on current settings
            const computedIRI = GS.computeIRI(this.props.schemeIRI, suggestedLabel, this.props.useSlashIRI, this.props.useUuidIRI, this.props.iriOptions);
            const labelIriOptions = Object.assign({
                prependText: this.props.iriOptions.xlOptions.prependText,
                appendText: this.props.iriOptions.xlOptions.shouldAppendText ? this.props.defaultLang : "",
                textSeparator: "-"
            }, this.props.iriOptions);
            const labelComputedIRI = GS.computeIRI(this.props.schemeIRI, suggestedLabel, this.props.useSlashIRI, this.props.useUuidIRI, labelIriOptions);
            suggestionInsertIRI = computedIRI;
            suggestionLabelInsertIRI = labelComputedIRI;
            suggestionParentConcept = this.props.id === this.props.schemeIRI ? null : this.props.id;
        }
        const iriID = uuidv4();
        const suggestionIRI = "https://graphologi.com/data/suggestion/" + iriID;
        let newSuggestion = {
            id: suggestionIRI,
            type: suggestionType,
            inScheme: [this.props.schemeIRI],
            suggestionTargetConcept,
            ...(this.props.optionForXL && suggestionType === "NewConceptSuggestion") && {
                useXL: this.props.useXL
            },
            ...(this.props.defaultClasses && suggestionType === "NewConceptSuggestion") && {
                defaultClass: this.props.defaultClasses
            },
            ...(suggestedLabel) && {
                prefLabel: { [this.props.defaultLang]: suggestedLabel }
            },
            suggestionParentConcept,
            suggestionInsertIRI,
            suggestionLabelInsertIRI,
            ...(assignee && assignee !== "All") && {
                assignmentNotification: notify,
                assignedUser: assignee,
                assignedLink: this.props.id
            },
            user: user.id,
            status: [STATUS_PENDING],
            comment: {
                [lang]: text
            }
        };
        await GS.insertResource(newSuggestion, suggestionIRI, this.props.graph);
        GS.addProperty(suggestionIRI, this.props.graph, "suggestionSourceConcept", this.props.id);
        const discussionID = uuidv4();
        const discussionIRI = "https://grafsync.graphifi.com/data/discussion/" + discussionID;
        let newDiscussion = {
            id: discussionIRI,
            type: "Discussion"
        };
        await GS.insertResource(newDiscussion, discussionIRI, this.props.graph);
        GS.addProperty(suggestionIRI, this.props.graph, "hasDiscussion", discussionIRI);
    };

    render() {

        const { data, graph, defaultLang, classes, isReadOnly, isVisible, components, useSlashIRI, useUuidIRI, projectIRI, useWorkflow,
            entitlements, schemeIRI, optionForXL, id, defaultType, monitorSuggestionStatus, useXL, iriOptions } = this.props;
        const isSchemeLevel = id === schemeIRI;

        const canMakeSuggestions = entitlements.includes(ENTITLEMENTS.MAKE_SUGGESTIONS) || entitlements.includes(ENTITLEMENTS.MANAGE_SUGGESTIONS);

        if (data._notAvailable) {
            if (isVisible) return <ActionText offset="30px">Suggestions are not available.</ActionText>;
            return null;
        }

        const isRejected = useWorkflow && data.status && data.status[0] === STATUS_REJECTED;

        const editable = isSchemeLevel ?
            !data.deprecated || isRejected
            :
            !(data.deprecated || isRejected || !data.inScheme || data.inScheme.length === 0 || (data.inScheme && !data.inScheme.includes(schemeIRI)));


        return (
            <UserConsumer>
                {({ user }) =>
                    <React.Fragment>
                        {isVisible &&
                            <React.Fragment>
                                {isSchemeLevel && (!data.isSuggestionSourceOf || data.isSuggestionSourceOf.length === 0) &&
                                    <div>
                                        <ActionText offset="80px">Select a concept to view the detail.</ActionText>
                                        {!isReadOnly && canMakeSuggestions &&
                                            <ActionText offset="30px">or</ActionText>
                                        }
                                    </div>
                                }
                                {(!isSchemeLevel || (data.isSuggestionSourceOf && data.isSuggestionSourceOf.length > 0)) &&
                                    <Typography paragraph className={classes.mainHeading} style={{ marginTop: 10 }}>Suggestions</Typography>
                                }
                                {!isSchemeLevel && data.deprecated &&
                                    <div style={{ marginBottom: 20 }}>
                                        <Information text="Suggestions are not editable for a deprecated concept." dataTest="deprecated-suggestions-not-editable" />
                                    </div>
                                }
                                {isRejected &&
                                    <div style={{ marginBottom: 20 }}>
                                        <Information text="Suggestions are not editable for a rejected concept." dataTest="rejected-suggestions-not-editable" />
                                    </div>
                                }
                                {!isSchemeLevel && (data.inScheme && !data.inScheme.includes(schemeIRI)) &&
                                    <div style={{ marginBottom: 20 }}>
                                        <Information text={
                                            schemeIRI ?
                                                "Suggestions are not editable for a concept that is not in the concept scheme."
                                                :
                                                "Suggestions are not editable in this view."
                                        } dataTest="notinscheme-suggestions-not-editable" />
                                    </div>
                                }
                                {!isSchemeLevel && (!data.inScheme || data.inScheme.length === 0) &&
                                    <div style={{ marginBottom: 20 }}>
                                        <Information text="Suggestions are not editable for a floating concept." dataTest="floating-suggestions-not-editable" />
                                    </div>
                                }
                                {isReadOnly && (!data.isSuggestionSourceOf || data.isSuggestionSourceOf.length === 0) &&
                                    <div style={{ marginBottom: 120 }}>
                                        <ActionText offset="120px">There are no suggestions.</ActionText>
                                    </div>
                                }
                                {!isReadOnly && !data.pendingDeletion && editable && canMakeSuggestions && schemeIRI &&
                                    <div className={data.isSuggestionSourceOf && data.isSuggestionSourceOf.length > 0 ?
                                        classes.existingSuggestions
                                        :
                                        classes.noSuggestions
                                    }>
                                        {(!data.isSuggestionSourceOf || data.isSuggestionSourceOf.length === 0) &&
                                            <div style={{ marginTop: 20, marginBottom: 20 }}>
                                                <img style={{ height: 150 }} src="/images/lightbulb.svg" alt="" />
                                                {isSchemeLevel ?
                                                    <ActionText>Do you think there should be a new top concept?</ActionText>
                                                    :
                                                    <ActionText>Have some thoughts on this concept? Why not add a suggestion.</ActionText>
                                                }
                                            </div>
                                        }
                                        <AddSuggestionDialog
                                            defaultType={defaultType}
                                            isSchemeLevel={isSchemeLevel}
                                            data={data}
                                            schemeIRI={schemeIRI}
                                            useWorkflow={useWorkflow}
                                            optionForXL={optionForXL}
                                            graph={graph}
                                            projectIRI={projectIRI}
                                            sourceConcept={data.id}
                                            defaultLang={defaultLang}
                                            onAddSuggestion={this.addSuggestion(user)} />
                                    </div>
                                }
                            </React.Fragment>
                        }
                        {data.isSuggestionSourceOf && data.isSuggestionSourceOf.length > 0 &&
                            <SuggestionsViewer
                                schemeIRI={schemeIRI}
                                isVisible={isVisible}
                                projectIRI={projectIRI}
                                components={components}
                                useWorkflow={useWorkflow}
                                optionForXL={optionForXL}
                                iriOptions={iriOptions}
                                isReadOnly={isReadOnly}
                                monitorSuggestionStatus={monitorSuggestionStatus}
                                useSlashIRI={useSlashIRI}
                                useUuidIRI={useUuidIRI}
                                useXL={useXL}
                                entitlements={entitlements}
                                defaultLang={defaultLang}
                                handleNotAvailable={true}
                                id={id}
                                graph={graph} />
                        }
                    </React.Fragment>
                }
            </UserConsumer>
        );
    }
}

SuggestionsManager.defaultProps = {
    isReadOnly: false,
    visible: false,
    optionForXL: false,
    useWorkflow: false,
    defaultClasses: [],
    useXL: false
};

SuggestionsManager.propTypes = {
    defaultType: PropTypes.string,
    schemeIRI: PropTypes.string,
    projectIRI: PropTypes.string.isRequired,
    isVisible: PropTypes.bool.isRequired,
    optionForXL: PropTypes.bool,
    useXL: PropTypes.bool,
    defaultClasses: PropTypes.array,
    useSlashIRI: PropTypes.bool,
    useUuidIRI: PropTypes.bool,
    useWorkflow: PropTypes.bool,
    monitorSuggestionStatus: PropTypes.func,
    components: PropTypes.array.isRequired,
    defaultLang: PropTypes.string.isRequired,
    data: PropTypes.object.isRequired,
    id: PropTypes.string.isRequired,
    entitlements: PropTypes.array.isRequired,
    graph: PropTypes.string.isRequired,
    isReadOnly: PropTypes.bool,
    iriOptions: PropTypes.object,
    classes: PropTypes.object
};

export default withStyles(styles)(withGS(SuggestionsManager, ["ALL"], null, {}, true));